/**
 * Eye Trackable Button component - A reusable button component with eye tracking support
 * Handles gaze events, mouse simulation, and dwell animations
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import './eye-trackable-button.scss';

/**
 * Global animation manager to ensure only one button animates at a time
 */
class DwellAnimationManager {
  private static instance: DwellAnimationManager;
  private currentAnimatingButton: string | null = null;
  private callbacks: Map<string, () => void> = new Map();

  static getInstance(): DwellAnimationManager {
    if (!DwellAnimationManager.instance) {
      DwellAnimationManager.instance = new DwellAnimationManager();
    }
    return DwellAnimationManager.instance;
  }

  registerButton(buttonId: string, stopCallback: () => void): void {
    this.callbacks.set(buttonId, stopCallback);
  }

  unregisterButton(buttonId: string): void {
    if (this.currentAnimatingButton === buttonId) {
      this.currentAnimatingButton = null;
    }
    this.callbacks.delete(buttonId);
  }

  startAnimation(buttonId: string): boolean {
    // Stop any currently animating button
    if (this.currentAnimatingButton && this.currentAnimatingButton !== buttonId) {
      const stopCallback = this.callbacks.get(this.currentAnimatingButton);
      if (stopCallback) {
        stopCallback();
      }
    }

    this.currentAnimatingButton = buttonId;
    return true;
  }

  stopAnimation(buttonId: string): void {
    if (this.currentAnimatingButton === buttonId) {
      this.currentAnimatingButton = null;
    }
  }

  isAnimating(buttonId: string): boolean {
    return this.currentAnimatingButton === buttonId;
  }
}

/**
 * Eye tracking system settings interface
 */
interface IEyeTrackingSettings {
  dwellTime: number;
  activationMethod: 'Gaze' | 'GazeAndSwitch' | 'GazeAndClick';
  settingsChangedEvent: string;
}

/**
 * Eye tracking system interface
 */
interface IEyeTrackingSystem {
  getSystemSettings: () => IEyeTrackingSettings;
}

/**
 * Extended window interface with eye tracking
 */
declare global {
  interface Window {
    eyeTracking?: IEyeTrackingSystem;
  }
}

/**
 * Button variant types for different visual styles
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'nav'
  | 'mode-selector'
  | 'action'
  | 'preview'
  | 'music';

/**
 * Button size types
 */
export type ButtonSize = 'small' | 'medium' | 'large';

/**
 * Props interface for the EyeTrackableButton component
 */
export interface IEyeTrackableButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  style?: React.CSSProperties;
  'data-testid'?: string;
  'data-gaze-region'?: string;
  'data-gaze-region-margin'?: string;
  allowOffscreenPosition?: boolean;
  ariaLabel?: string;
  title?: string;
}

/**
 * EyeTrackableButton - A reusable button component with comprehensive eye tracking support
 *
 * Features:
 * - Eye tracking event handling (gazeover, gazeout, gazeactivate)
 * - Mouse simulation for development/testing
 * - Dwell animation with visual feedback
 * - Responsive sizing according to accessibility requirements
 * - Multiple visual variants
 * - Proper event cleanup and error handling
 */
export const EyeTrackableButton: React.FC<IEyeTrackableButtonProps> = ({
  children,
  onClick,
  disabled = false,
  variant = 'primary',
  size = 'medium',
  className = '',
  style,
  'data-testid': dataTestId,
  'data-gaze-region': dataGazeRegion = 'true',
  'data-gaze-region-margin': dataGazeRegionMargin,
  allowOffscreenPosition,
  ariaLabel,
  title,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dwellTimeoutRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const dwellStartTimeRef = useRef<number | null>(null);
  const buttonIdRef = useRef<string>(`btn-${Math.random().toString(36).substr(2, 9)}`);
  const animationManager = DwellAnimationManager.getInstance();
  const [isGazeActive, setIsGazeActive] = useState(false);
  const [isDwelling, setIsDwelling] = useState(false);
  const [dwellProgress, setDwellProgress] = useState(0);
  const [eyeTrackingSettings, setEyeTrackingSettings] = useState<IEyeTrackingSettings>({
    dwellTime: 1000,
    activationMethod: 'Gaze',
    settingsChangedEvent: 'eyeTrackingSettingsChanged',
  });

  // Check if we should simulate gaze with mouse events
  const shouldSimulateGaze = window.location.href.includes('simulategaze');

  /**
   * Load eye tracking settings from the system
   */
  const loadEyeTrackingSettings = useCallback(() => {
    try {
      if (window.eyeTracking?.getSystemSettings) {
        const settings = window.eyeTracking.getSystemSettings();
        setEyeTrackingSettings(settings);
      }
    } catch (error) {
      console.warn('Failed to load eye tracking settings:', error);
    }
  }, []);

  /**
   * Handle settings change events
   */
  const handleSettingsChange = useCallback(() => {
    loadEyeTrackingSettings();
  }, [loadEyeTrackingSettings]);

  /**
   * Clear any active dwell timeout and animation
   */
  const clearDwellTimeout = useCallback(() => {
    if (dwellTimeoutRef.current) {
      clearTimeout(dwellTimeoutRef.current);
      dwellTimeoutRef.current = null;
    }
  }, []);

  /**
   * Stop dwell animation and reset progress
   */
  const stopDwellAnimation = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    dwellStartTimeRef.current = null;

    // Notify animation manager
    animationManager.stopAnimation(buttonIdRef.current);

    // Immediately set progress to 0 to stop animation visually
    setDwellProgress(0);
  }, [animationManager]);

  /**
   * Start dwell animation using requestAnimationFrame
   */
  const startDwellAnimation = useCallback(() => {
    if (!eyeTrackingSettings?.dwellTime) return;

    // Register with animation manager and stop any other animations
    if (!animationManager.startAnimation(buttonIdRef.current)) {
      return; // Another animation is already running
    }

    dwellStartTimeRef.current = performance.now();
    setDwellProgress(0);

    const animateDwell = (currentTime: number) => {
      // Check if animation was cancelled
      if (!dwellStartTimeRef.current || animationFrameRef.current === null) {
        return;
      }

      // Check if we're still the active animation
      if (!animationManager.isAnimating(buttonIdRef.current)) {
        // Animation was stopped by manager, clean up our state
        setIsDwelling(false);
        setIsGazeActive(false);
        setDwellProgress(0);
        dwellStartTimeRef.current = null;
        animationFrameRef.current = null;
        return;
      }

      const elapsed = currentTime - dwellStartTimeRef.current;
      const progress = Math.min(elapsed / eyeTrackingSettings.dwellTime, 1);

      setDwellProgress(progress);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animateDwell);
      } else {
        // Animation complete - trigger activation
        // only simulate activation if simulating gaze via mouse
        // for other use cases the gazeactivate event will handle it
        if (!disabled && shouldSimulateGaze) {
          onClick();
        }
        setIsDwelling(false);
        setIsGazeActive(false);
        setDwellProgress(0);
        dwellStartTimeRef.current = null;
        animationFrameRef.current = null;
        animationManager.stopAnimation(buttonIdRef.current);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animateDwell);
  }, [eyeTrackingSettings?.dwellTime, disabled, onClick, animationManager]);

  /**
   * Handle gaze enter event
   */
  const handleGazeOver = useCallback(() => {
    if (disabled) return;

    setIsGazeActive(true);

    // Don't start dwell animation for GazeAndSwitch activation method
    if (eyeTrackingSettings?.activationMethod === 'GazeAndSwitch') {
      return;
    }

    setIsDwelling(true);
    startDwellAnimation();
  }, [disabled, eyeTrackingSettings?.activationMethod, startDwellAnimation]);

  /**
   * Handle gaze leave event
   */
  const handleGazeOut = useCallback(() => {
    // First stop animation and clear timeout
    stopDwellAnimation();
    clearDwellTimeout();

    // Then update states
    setIsGazeActive(false);
    setIsDwelling(false);
  }, [stopDwellAnimation, clearDwellTimeout]); /**
   * Handle gaze activation event
   */
  const handleGazeActivate = useCallback(() => {
    if (!disabled) {
      onClick();
    }
    setIsDwelling(false);
    setIsGazeActive(false);
    stopDwellAnimation();
  }, [disabled, onClick, stopDwellAnimation]);

  /**
   * Handle click event
   */
  const handleClick = useCallback(() => {
    if (!disabled) {
      onClick();
    }
  }, [disabled, onClick]);

  // Initialize eye tracking settings on mount
  useEffect(() => {
    loadEyeTrackingSettings();
  }, [loadEyeTrackingSettings]);

  // Register with animation manager
  useEffect(() => {
    animationManager.registerButton(buttonIdRef.current, stopDwellAnimation);

    return () => {
      animationManager.unregisterButton(buttonIdRef.current);
    };
  }, [animationManager, stopDwellAnimation]);

  // Subscribe to settings changes
  useEffect(() => {
    const settingsEvent = eyeTrackingSettings?.settingsChangedEvent || 'eyeTrackingSettingsChanged';

    window.addEventListener(settingsEvent, handleSettingsChange);

    return () => {
      window.removeEventListener(settingsEvent, handleSettingsChange);
    };
  }, [eyeTrackingSettings?.settingsChangedEvent, handleSettingsChange]);

  // Setup gaze event listeners
  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    console.log('Setting up eye tracking event listeners');

    // Mouse simulation for development
    if (shouldSimulateGaze) {
      button.addEventListener('mouseenter', handleGazeOver);
      button.addEventListener('mouseleave', handleGazeOut);
    } else {
      // Eye tracking events
      button.addEventListener('gazeover', handleGazeOver);
      button.addEventListener('gazeout', handleGazeOut);
      button.addEventListener('gazeactivate', handleGazeActivate);
    }

    return () => {
      console.log('Cleaning up eye tracking event listeners');
      // Cleanup event listeners
      if (button) {
        button.removeEventListener('gazeover', handleGazeOver);
        button.removeEventListener('gazeout', handleGazeOut);
        button.removeEventListener('gazeactivate', handleGazeActivate);

        if (shouldSimulateGaze) {
          button.removeEventListener('mouseenter', handleGazeOver);
          button.removeEventListener('mouseleave', handleGazeOut);
        }
      }
    };
  }, [handleGazeOver, handleGazeOut, handleGazeActivate, shouldSimulateGaze]);

  // Cleanup timeout and animation on unmount
  useEffect(() => {
    return () => {
      clearDwellTimeout();
      stopDwellAnimation();
    };
  }, [clearDwellTimeout, stopDwellAnimation]);

  // Reset progress immediately when dwelling stops
  useEffect(() => {
    if (!isDwelling) {
      setDwellProgress(0);
    }
  }, [isDwelling]);

  // Compute CSS classes
  const buttonClasses = [
    'eye-trackable-button',
    `eye-trackable-button--${variant}`,
    `eye-trackable-button--${size}`,
    isGazeActive ? 'eye-trackable-button--gaze-active' : '',
    isDwelling ? 'eye-trackable-button--dwelling' : '',
    disabled ? 'eye-trackable-button--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Compute CSS custom properties for dwell animation and gaze states
  const buttonStyle: React.CSSProperties & { [key: string]: string | number } = {
    '--dwell-duration': `${eyeTrackingSettings.dwellTime}ms`,
    '--dwell-progress': dwellProgress,
    '--is-gaze-active': isGazeActive ? '1' : '0',
    '--is-dwelling': isDwelling ? '1' : '0',
    ...style,
  };

  return (
    <button
      ref={buttonRef}
      className={buttonClasses}
      style={buttonStyle}
      onClick={handleClick}
      disabled={disabled}
      data-testid={dataTestId}
      data-gaze-region={dataGazeRegion}
      data-gaze-region-margin={dataGazeRegionMargin}
      data-gaze-region-allow-offscreen-position={allowOffscreenPosition}
      aria-label={ariaLabel}
      title={title}
    >
      <span className='eye-trackable-button__content'>{children}</span>
    </button>
  );
};
